import { Modal } from "antd";
import { Megaphone, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NEWS_MODAL } from "../data/campaigns";
import { useCampaigns } from "../hooks/useCampaigns";
import { Campaign } from "../types";
import { CampaignCard } from "./CampaignCard";
import { NewsIllustration } from "./NewsIllustration";

export type CampaignSurface = "landing" | "home";

export function CampaignModal({ surface }: { surface: CampaignSurface }) {
  const navigate = useNavigate();
  const { campaigns, dismiss } = useCampaigns(surface);

  if (!campaigns.length) return null;

  const shownIds = campaigns.map((c) => c.id);
  const close = () => dismiss(shownIds);
  const open = (campaign: Campaign) => {
    dismiss(shownIds);
    navigate(campaign.href);
  };

  return (
    <Modal
      open
      centered
      width={720}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
      styles={{
        content: {
          padding: 0,
          overflow: "hidden",
          borderRadius: 24,
          border: "1px solid rgb(var(--c-commerce) / 0.25)",
          boxShadow: "0 24px 64px rgba(16, 24, 40, 0.16)",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
        },
      }}
    >
      <section aria-label="Novidades" className="relative bg-surface px-5 pb-5 pt-6 font-sans sm:px-8 sm:pb-8 sm:pt-8">
        <button
          type="button"
          aria-label="Fechar novidades"
          onClick={close}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-edge bg-surface text-ink-muted transition hover:bg-surface-hover hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-start gap-6 pr-10 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:pr-0">
          <div className="min-w-0 flex-1 text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-commerce-soft px-3 py-1 text-xs font-semibold text-commerce-ink">
              <Megaphone className="h-3.5 w-3.5" strokeWidth={2.25} />
              {NEWS_MODAL.pill}
            </span>
            <h2 className="mt-3 text-[1.6rem] font-bold leading-tight tracking-tight text-ink sm:mt-4 sm:text-[2rem]">
              {NEWS_MODAL.title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-ink-secondary sm:mt-3 sm:text-[15px]">{NEWS_MODAL.subtitle}</p>
          </div>
          <NewsIllustration />
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:mt-7">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} onOpen={open} />
          ))}
        </div>
      </section>
    </Modal>
  );
}
